import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { useDataSource } from "@/adapters/data-source";
import { formatDate } from "@/components/table/cells";
import { runNote } from "@/modules/orders/tracking";
import { RunStatusBadge } from "@/modules/settings/logistics/RecentChecks";
import { useSourceNames } from "@/modules/settings/logistics/useSourceNames";

/** How many of the latest runs are looked through for a problem. */
const LOOKED_AT = 25;
/** How many problems are listed before the rest are one link away. */
const SHOWN = 3;

/**
 * Failed shipment-tracking checks, on the Shopify Sync screen (#527, Part B).
 *
 * That screen is where the state of the channel boundary is read, and a
 * tracking check that could not reach Shopify or iThink belongs in the same
 * glance: a run that fails is a row there, not a line in a container log. The
 * latest few runs are looked through and any that failed, or only partly
 * worked, are listed with their words; the full history, the partners and the
 * rules are on Settings → Logistics partners, one link away.
 *
 * It never takes the Sync page down with it: reading runs is the same
 * `settings.sync.view` the page itself needs, but a read that fails here says
 * so in a line and leaves the sweeps and the write log where they were.
 */
export function TrackingProblems() {
  const data = useDataSource();
  const nameOf = useSourceNames();
  const query = useQuery({
    queryKey: ["tracking", "runs", "sync-screen"],
    queryFn: () => data.tracking.listRuns({ limit: LOOKED_AT }),
  });

  const problems = (query.data?.items ?? []).filter(
    (run) => run.status === "failed" || run.status === "partial",
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="max-w-prose">
          <h2 className="text-sm font-semibold">Shipment tracking</h2>
          <p className="text-muted-foreground text-sm">
            Checks of where each parcel is, through the courier partner or
            Shopify&apos;s scan history. Failures among the latest {LOOKED_AT}{" "}
            checks are listed here.
          </p>
        </div>
        <Link
          to="/settings/logistics"
          className="text-primary-strong inline-flex min-h-9 items-center text-sm font-medium underline-offset-2 hover:underline pointer-coarse:min-h-11"
        >
          Logistics partners and recent checks
        </Link>
      </div>

      {query.isPending ? (
        <p className="text-muted-foreground text-sm">Reading the checks…</p>
      ) : query.isError ? (
        <p className="text-muted-foreground text-sm">
          The tracking checks could not be read just now. They are on Logistics
          partners.
        </p>
      ) : problems.length === 0 ? (
        <p className="text-sm">
          No failed tracking checks among the latest {LOOKED_AT}.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {problems.slice(0, SHOWN).map((run) => (
            <li key={run.id} className="space-y-1 px-3 py-2.5 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="tabular-nums">
                  {formatDate(run.startedAt, true)} · {nameOf(run.source)}
                </span>
                <RunStatusBadge status={run.status} />
              </div>
              {runNote(run) ? (
                <p className="text-muted-foreground wrap-anywhere">
                  {runNote(run)}
                </p>
              ) : null}
            </li>
          ))}
          {problems.length > SHOWN ? (
            <li className="text-muted-foreground px-3 py-2 text-sm">
              and {problems.length - SHOWN} more among the latest {LOOKED_AT}
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
